/**
 * L'horloge du moteur — SPEC-MOTEUR §2.1, amendée par ⟦A-19⟧.
 *
 * Le temps n'avance **que** quand on agit (PLAN §4.1). `clock_h` compte **toutes** les heures
 * du calendrier simulé — les heures de travail **et** les 8 h de la nuit.
 *
 * ── ⟦A-19⟧ Ce qui a changé, et pourquoi ───────────────────────────────────────
 *
 * *« Une nuit qui ne fait pas avancer l'heure, ça ne va pas du tout. Ça doit coûter des
 * heures, parce qu'une nuit, c'est ce qui doit faire avancer les choses, c'est ce qui doit
 * engendrer que d'autres avancent, qu'il y a des trucs qui se perdent. »* (Mathieu)
 *
 * **Avant** : `clock_h` ne comptait que les heures ÉVEILLÉES et `sleep` coûtait 0 h. Dormir
 * ne franchissait donc **aucune** frontière de jour : ni rente evergreen, ni désabonnements,
 * ni decay de momentum, ni péremption d'idées, ni progression du rival ⟦A-15⟧ — au moment
 * précis où le joueur s'attend à ce que le monde tourne. Le récapitulatif ⟦A-16⟧ ne s'ouvrait
 * jamais sur une nuit.
 *
 * **Après** : la nuit coûte `H_NUIT` heures, et la journée en fait `H_JOUR` = 24.
 *
 * ── Les DEUX horloges, et il ne faut jamais les confondre ─────────────────────
 *
 * | constante | ce qu'elle mesure | qui la lit |
 * |---|---|---|
 * | `H_JOUR` (24) | le **calendrier** : une journée pleine, nuit comprise | frontières de jour, charges, durées de règles (crise, strike, faillite…) |
 * | `H_EVEILLE_PAR_JOUR` (16) | le **budget de travail** d'une journée | le dépassement d'heures ⟦A-18⟧, et lui seul |
 *
 * Les mélanger est LE piège de cet avenant : facturer le dépassement au-delà de 24 h
 * supprimerait la fatigue (personne ne tient 24 h de travail d'affilée), et compter les
 * frontières de jour tous les 16 h ferait tomber les charges une fois et demie par jour.
 *
 * ⚠️ **Toute durée de règle se dérive de `H_JOUR`, jamais écrite en heures absolues.** C'est
 * la seule chose qui peut mal tourner en silence ici : « 224 h » voulait dire 14 jours quand
 * la journée en faisait 16, et n'en vaudrait plus que 9,3 aujourd'hui.
 */
/** ⟦A-19⟧ Heures de travail disponibles dans une journée. Au-delà : dépassement ⟦A-18⟧. */
export const H_EVEILLE_PAR_JOUR = 16;
/** ⟦A-19⟧ Durée d'une nuit de sommeil, en heures de calendrier. C'est le coût de `sleep`. */
export const H_NUIT = 8;
/** Jour simulé = 24 h : `H_EVEILLE_PAR_JOUR` de travail + `H_NUIT` de nuit ⟦A-19⟧. */
export const H_JOUR = H_EVEILLE_PAR_JOUR + H_NUIT;
/** Semaine simulée = 7 jours. */
export const H_SEMAINE = 7 * H_JOUR; // 168 h
/** Mois simulé = 30 jours. */
export const H_MOIS = 30 * H_JOUR; // 720 h
/**
 * Durées de règles exprimées en jours, converties une fois pour toutes (§2.1) :
 * demi-vie evergreen 90 j, crise 14 j, faillite 30 j, strike 90 j, thème épuisé 14 j.
 */
export const H_DEMI_VIE_EVERGREEN = 90 * H_JOUR; // 2 160 h
export const H_CRISE = 14 * H_JOUR; // 336 h
export const H_FAILLITE = 30 * H_JOUR; // 720 h
export const H_STRIKE = 90 * H_JOUR; // 2 160 h
export const H_THEME_EPUISE = 14 * H_JOUR; // 336 h
/** Anti-frustration : 3 semaines sans palier (§10.4). */
export const H_STAGNATION = 3 * H_SEMAINE; // 504 h
/**
 * Fenêtre d'opportunité ×3 (§4.5, arbitrage A22) — PLAN §4.8 : « ×3 pendant 24 h simulées ».
 * ⟦A-19⟧ Ces 24 h sont désormais **exactement une journée**, ce qu'elles voulaient dire :
 * la constante ne change pas de valeur, elle cesse d'être un nombre absolu.
 */
export const H_WINDOW_BOOST = H_JOUR; // 24 h
/** « Laisser retomber » avant la vidéo d'excuses (§11.2) : 2 jours. */
export const H_EXCUSES_A_FROID = 2 * H_JOUR; // 48 h
/**
 * Momentum : silence toléré avant décroissance — **3 jours simulés** (PLAN §4.2 : « publier
 * ≤ 3 j simulés d'écart (+), silence −0,05/jour »).
 *
 * ⟦A-19⟧ Écrit `48` en dur jusqu'ici, ce qui valait bien 3 jours quand la journée en faisait
 * 16 — et n'en aurait plus valu que 2. C'est exactement la dérive silencieuse que l'avenant
 * demande de traquer : la règle est en JOURS, la constante l'est maintenant aussi.
 */
export const H_SILENCE_TOLERE = 3 * H_JOUR; // 72 h
/** Jour simulé courant : `floor(clock_h / H_JOUR)` (§2.1). */
export function jour(clockH) {
    return Math.floor(clockH / H_JOUR);
}
/**
 * Frontières de jour franchies par un pas d'horloge `from → to` (§2.3 pas 5).
 * Retourne les **numéros de jour** atteints, dans l'ordre croissant. Un pas qui ne
 * franchit aucun multiple de `H_JOUR` rend un tableau vide ; un pas long (pause =
 * `H_SEMAINE`) en rend plusieurs, à traiter dans l'ordre — le déterminisme en dépend.
 *
 * ⟦A-19⟧ **Une nuit en franchit désormais une** : c'est tout l'objet de l'avenant.
 */
export function frontieresDeJour(from, to) {
    if (to <= from)
        return [];
    const premier = Math.floor(from / H_JOUR) + 1;
    const dernier = Math.floor(to / H_JOUR);
    const jours = [];
    for (let j = premier; j <= dernier; j++)
        jours.push(j);
    return jours;
}
/** Nombre de semaines simulées entières écoulées entre deux instants. */
export function semainesEcoulees(from, to) {
    if (to <= from)
        return 0;
    return Math.floor(to / H_SEMAINE) - Math.floor(from / H_SEMAINE);
}
/** Pro rata hebdomadaire d'une durée en heures simulées — base de §4.7 et §6.3. */
export function prorataHebdo(deltaH) {
    return deltaH / H_SEMAINE;
}
//# sourceMappingURL=clock.js.map